import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Avatar, Typography, Button, Chip, TextField, Card, CardContent,
  CircularProgress, Stack, alpha, Divider, Drawer, IconButton,
  useTheme, useMediaQuery, Paper,
} from "@mui/material";
import {
  LocationOn, Message, Schedule, Search, PersonOutline, School,
  EmojiEvents, ArrowBack, ArrowForward, Close, Groups,
  HourglassEmpty, CheckCircleOutline,
} from "@mui/icons-material";
import Footer from "../globalComponents/footer";
import TopBar from "../globalComponents/topbar";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API = "https://backend-global-1vq9.onrender.com";

// ── Helper: build a full URL from a server-stored image path ─────────────────
// Same pattern used everywhere else — handles relative paths from old local
// uploads and full Cloudinary URLs from new ones without any extra logic.
const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:") || path.startsWith("blob:")) return path;
  return `${API}/${String(path).replace(/\\/g, "/").replace(/^\//, "")}`;
};

export default function MenteeDashboard() {
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const learnersPerPage = 4;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // ── Fetch accepted-only mentees ────────────────────────────────────────
  // Step 1: get this mentor's connection requests, filter to accepted, collect menteeIds.
  // Step 2: fetch the full learner list, then keep only learners whose userId is in that set.
  // Done in parallel via Promise.all so we don't wait sequentially.
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const user = JSON.parse(localStorage.getItem("user"));
        const mentorId = user?.id || user?._id;
        if (!mentorId) {
          setError("Mentor not found. Please sign in again.");
          setLoading(false);
          return;
        }

        const [requestsRes, learnersRes] = await Promise.all([
          axios.get(`${API}/api/connection-requests/mentor/${mentorId}`),
          axios.get(`${API}/api/learner/list`),
        ]);

        const acceptedIds = new Set(
          (requestsRes.data?.data || [])
            .filter((r) => (r.status || "").toLowerCase() === "accepted")
            .map((r) => r.menteeId)
        );

        const allLearners = learnersRes.data?.learners || [];
        const acceptedMentees = allLearners.filter((l) => acceptedIds.has(l.userId));

        setLearners(acceptedMentees);
        if (acceptedMentees.length > 0) setSelectedLearner(acceptedMentees[0]);
      } catch (err) {
        setError("Failed to load mentees. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filter + Paginate ──────────────────────────────────────────────────
  const filteredLearners = learners.filter((l) =>
    l.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const startIndex = page * learnersPerPage;
  const endIndex = startIndex + learnersPerPage;
  const paginatedLearners = filteredLearners.slice(startIndex, endIndex);
  const hasNext = endIndex < filteredLearners.length;
  const hasPrev = page > 0;

  // ── Detail panel — shared between desktop panel and mobile drawer ──────
  const DetailPanel = () => {
    if (!selectedLearner) return (
      <Box height="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <Box sx={{
          width: 80, height: 80, borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
          display: "flex", alignItems: "center", justifyContent: "center", mb: 3,
          boxShadow: `0 8px 20px ${C.primary}30`,
        }}>
          <PersonOutline sx={{ fontSize: 36, color: "#fff" }} />
        </Box>
        <Typography variant="h6" sx={{ color: C.textSecondary, fontWeight: 600, textAlign: "center" }}>
          Select a mentee to view details
        </Typography>
      </Box>
    );

    const selectedAvatarUrl = resolveImageUrl(selectedLearner.profileImage);

    return (
      <>
        {/* Panel header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
              width: 6, height: 24,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              borderRadius: 1, flexShrink: 0,
            }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: C.textPrimary, fontSize: { xs: 15, sm: 17 } }}>
              Mentee Profile
            </Typography>
          </Stack>
          {isMobile && (
            <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: C.textSecondary }}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Profile gradient banner */}
        <Box sx={{
          position: "relative",
          p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3,
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
          boxShadow: `0 8px 24px ${C.primary}30`,
          overflow: "hidden",
        }}>
          <Box sx={{
            position: "absolute",
            top: "-50%", right: "-15%",
            width: 160, height: 160,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Avatar
              src={selectedAvatarUrl}
              alt={selectedLearner.fullName || "Mentee"}
              sx={{
                width: { xs: 56, sm: 80 }, height: { xs: 56, sm: 80 },
                bgcolor: "#fff", color: C.primary,
                fontSize: { xs: 22, sm: 32 }, fontWeight: 700,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)", flexShrink: 0,
              }}
            >
              {!selectedAvatarUrl && (selectedLearner.fullName?.charAt(0).toUpperCase() || "?")}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography sx={{
                fontWeight: 700, fontSize: { xs: 16, sm: 20 },
                color: "#fff", mb: 0.5,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {selectedLearner.fullName}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <LocationOn sx={{ fontSize: 15, color: "rgba(255,255,255,0.9)" }} />
                <Typography variant="body2" sx={{
                  color: "rgba(255,255,255,0.9)", fontWeight: 500, fontSize: { xs: 12, sm: 14 },
                }}>
                  {selectedLearner.location || "Location not specified"}
                </Typography>
              </Stack>
              <Chip
                icon={<CheckCircleOutline sx={{ fontSize: "13px !important", color: "#fff !important" }} />}
                label="Connected"
                size="small"
                sx={{
                  bgcolor: alpha(C.secondary, 0.85),
                  color: "#fff", fontWeight: 700, height: 22,
                  fontSize: { xs: 10, sm: 12 },
                  "& .MuiChip-icon": { color: "#fff" },
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Detail fields */}
        <Stack spacing={2.5}>
          {[
            { label: "Email Address", value: selectedLearner.email, icon: null },
            { label: "Education Level", value: selectedLearner.educationLevel || "Not provided",
              icon: <School sx={{ fontSize: 18, color: C.primary }} /> },
            { label: "Preferred Mentor Type", value: selectedLearner.preferredMentorType || "Not specified", icon: null },
          ].map((field, i) => (
            <React.Fragment key={i}>
              <Box>
                <Typography variant="overline" sx={{ color: C.textMuted, fontWeight: 700, letterSpacing: 1, fontSize: "0.7rem" }}>
                  {field.label}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  {field.icon}
                  <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, fontSize: { xs: 13, sm: 15 } }}>
                    {field.value}
                  </Typography>
                </Stack>
              </Box>
              <Divider sx={{ borderColor: C.borderSoft }} />
            </React.Fragment>
          ))}

          {/* Fields of Interest */}
          <Box>
            <Typography variant="overline" sx={{ color: C.textMuted, fontWeight: 700, letterSpacing: 1, fontSize: "0.7rem" }}>
              Fields of Interest
            </Typography>
            <Box mt={1}>
              {selectedLearner.fieldOfInterest?.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedLearner.fieldOfInterest.map((field, idx) => (
                    <Chip
                      key={idx}
                      label={field}
                      size="small"
                      sx={{
                        bgcolor: C.primarySoft, color: C.primary,
                        fontWeight: 700,
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        py: 1.5,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: C.textMuted, fontStyle: "italic", fontSize: { xs: 12, sm: 14 } }}>
                  No interests listed
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: C.borderSoft }} />

          {/* Goals */}
          <Box>
            <Typography variant="overline" sx={{ color: C.textMuted, fontWeight: 700, letterSpacing: 1, fontSize: "0.7rem" }}>
              Goals
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start" mt={0.5}>
              <EmojiEvents sx={{ fontSize: 18, color: C.secondary, mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 500, lineHeight: 1.7, fontSize: { xs: 12, sm: 14 } }}>
                {selectedLearner.goals || "No goals provided"}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 3, borderColor: C.borderSoft }} />

        {/* Action buttons */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            fullWidth
            sx={{
              py: { xs: 1, sm: 1.5 }, textTransform: "none", borderRadius: 2.5,
              borderWidth: 1.5, borderColor: C.primary, color: C.primary,
              fontWeight: 700, fontSize: { xs: "0.85rem", sm: "0.95rem" },
              "&:hover": {
                borderWidth: 1.5, borderColor: C.primaryDark,
                bgcolor: C.primarySoft,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Schedule Meeting
          </Button>
          <Button
            onClick={() => navigate("/chat")}
            variant="contained"
            startIcon={<Message />}
            fullWidth
            sx={{
              py: { xs: 1, sm: 1.5 }, textTransform: "none", borderRadius: 2.5,
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              fontWeight: 700, fontSize: { xs: "0.85rem", sm: "0.95rem" },
              boxShadow: `0 4px 14px ${C.primary}30`,
              "&:hover": {
                boxShadow: `0 6px 18px ${C.primary}45`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Send Message
          </Button>
        </Stack>
      </>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg, overflowX: "hidden", width: "100%", boxSizing: "border-box" }}>
      <TopBar />

      <Box sx={{ flex: 1, pt: { xs: 2, sm: 3 }, pb: { xs: 4, sm: 6 } }}>
        <Box sx={{ maxWidth: "1400px", mx: "auto", px: { xs: "12px", sm: "20px", md: "32px" }, width: "100%", boxSizing: "border-box" }}>

          {/* ── Hero header ────────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              borderRadius: 3, overflow: "hidden",
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              p: { xs: 3, sm: 4 },
              mb: { xs: 2.5, sm: 3.5 },
              color: "#fff",
              boxShadow: `0 6px 24px ${C.primary}30`,
            }}
          >
            <Box sx={{
              position: "absolute",
              top: "-40%", right: "-10%",
              width: { xs: 180, sm: 240 }, height: { xs: 180, sm: 240 },
              borderRadius: "50%",
              background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
              pointerEvents: "none",
            }} />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                <Groups sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
                <Typography sx={{
                  fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
                }}>
                  Mentor Console
                </Typography>
              </Stack>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
              }}>
                My Mentees
              </Typography>
              <Typography sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5,
              }}>
                Learners who have connected with you. Schedule meetings, send messages, and track their journey.
              </Typography>

              {learners.length > 0 && (
                <Chip
                  icon={<CheckCircleOutline sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                  label={`${learners.length} connected mentee${learners.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{
                    mt: 2,
                    bgcolor: alpha(C.secondary, 0.85),
                    color: "#fff", fontWeight: 700,
                    "& .MuiChip-icon": { color: "#fff" },
                  }}
                />
              )}
            </Box>
          </Paper>

          {/* ── Main layout ───────────────────────────────────────────── */}
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 2, sm: 3 },
            width: "100%", boxSizing: "border-box",
          }}>

            {/* ── LEFT: Mentee list ────────────────────────────────── */}
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                width: { xs: "100%", lg: "380px" },
                flexShrink: 0,
                bgcolor: C.paper,
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                p: { xs: 2, sm: 3 },
                display: "flex", flexDirection: "column",
                maxHeight: { lg: "85vh" },
                boxSizing: "border-box",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
                },
              }}
            >
              {/* List header */}
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, mt: 0.5 }}>
                <Typography variant="h6" fontWeight={700} sx={{
                  color: C.textPrimary, flex: 1, fontSize: { xs: 15, sm: 16 },
                  letterSpacing: -0.2,
                }}>
                  Mentees
                </Typography>
                <Chip
                  label={filteredLearners.length}
                  size="small"
                  sx={{
                    bgcolor: C.primarySoft, color: C.primary, fontWeight: 700,
                    height: 22,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Stack>

              {/* Search */}
              <TextField
                variant="outlined"
                placeholder="Search mentees…"
                size="small"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: <Search fontSize="small" sx={{ mr: 1, color: C.textMuted }} />,
                }}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: C.primarySofter,
                    fontSize: 14,
                    "& fieldset": { borderColor: C.border },
                    "&:hover fieldset": { borderColor: C.primary },
                    "&.Mui-focused": {
                      bgcolor: C.paper,
                      "& fieldset": { borderColor: C.primary, borderWidth: 2 },
                    },
                  },
                }}
              />

              {/* Mentee cards */}
              <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress sx={{ color: C.primary }} />
                  </Box>
                ) : error ? (
                  <Box sx={{
                    textAlign: "center", py: 4, px: 2,
                    bgcolor: C.errorSoft, borderRadius: 2.5,
                    border: `1px solid ${alpha(C.error, 0.2)}`,
                  }}>
                    <Typography sx={{ color: C.error, fontWeight: 600, fontSize: 13 }}>{error}</Typography>
                  </Box>
                ) : learners.length === 0 ? (
                  // Empty state when the mentor has zero accepted connections
                  <Box sx={{
                    textAlign: "center", py: 5, px: 2,
                    bgcolor: C.primarySofter, borderRadius: 3,
                  }}>
                    <Box sx={{
                      width: 64, height: 64, borderRadius: "50%",
                      background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto", mb: 2,
                      boxShadow: `0 6px 16px ${C.primary}30`,
                    }}>
                      <HourglassEmpty sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Typography sx={{ color: C.textPrimary, fontWeight: 700, mb: 1, fontSize: 14 }}>
                      No mentees yet
                    </Typography>
                    <Typography sx={{ color: C.textSecondary, fontSize: 12, lineHeight: 1.6, maxWidth: 260, mx: "auto" }}>
                      You'll see learners here once you accept their connection requests.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate("/mentorsiderequest")}
                      sx={{
                        mt: 2.5,
                        bgcolor: C.primary, color: "#fff",
                        textTransform: "none", fontWeight: 700, fontSize: 12.5,
                        borderRadius: 2, px: 2.25, py: 0.85,
                        boxShadow: "none",
                        "&:hover": { bgcolor: C.primaryDark, boxShadow: `0 4px 12px ${C.primary}30` },
                      }}
                    >
                      View Requests
                    </Button>
                  </Box>
                ) : paginatedLearners.length === 0 ? (
                  // Filtered down to nothing by search
                  <Box sx={{
                    textAlign: "center", py: 6, px: 2,
                    bgcolor: C.primarySofter, borderRadius: 3,
                  }}>
                    <PersonOutline sx={{ fontSize: 44, color: C.textMuted, opacity: 0.5, mb: 1.5 }} />
                    <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: 13 }}>
                      No matches
                    </Typography>
                    <Typography sx={{ color: C.textSecondary, fontSize: 12, mt: 0.5 }}>
                      Try a different search
                    </Typography>
                  </Box>
                ) : (
                  paginatedLearners.map((learner) => {
                    const isSelected = selectedLearner?.userId === learner.userId;
                    const initial = learner.fullName?.charAt(0).toUpperCase() || "?";
                    const avatarUrl = resolveImageUrl(learner.profileImage);

                    return (
                      <Card
                        key={learner.userId}
                        elevation={0}
                        onClick={() => {
                          setSelectedLearner(learner);
                          if (isMobile) setDrawerOpen(true);
                        }}
                        sx={{
                          mb: 1.25,
                          border: `1.5px solid ${isSelected ? C.primary : C.borderSoft}`,
                          borderRadius: 2.5, cursor: "pointer",
                          transition: "all 0.2s ease",
                          bgcolor: isSelected ? C.primarySofter : C.paper,
                          boxSizing: "border-box",
                          "&:hover": {
                            borderColor: C.primary,
                            bgcolor: C.primarySofter,
                            transform: "translateX(2px)",
                            boxShadow: "0 4px 12px rgba(10,46,138,0.08)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="flex-start">
                            <Avatar
                              src={avatarUrl}
                              alt={learner.fullName || "Mentee"}
                              sx={{
                                width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 },
                                background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                                color: "#fff",
                                fontWeight: 700, fontSize: { xs: "1rem", sm: "1.15rem" },
                                boxShadow: `0 4px 10px ${C.primary}25`, flexShrink: 0,
                              }}
                            >
                              {!avatarUrl && initial}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5} flexWrap="wrap" gap={0.5}>
                                <Typography sx={{
                                  fontWeight: 700, color: C.textPrimary,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  fontSize: { xs: 12, sm: 14 },
                                }}>
                                  {learner.fullName}
                                </Typography>
                                <Chip
                                  label="Connected"
                                  size="small"
                                  sx={{
                                    height: 18, fontSize: "0.62rem", fontWeight: 700,
                                    bgcolor: C.secondarySoft, color: C.secondary,
                                    "& .MuiChip-label": { px: 0.6 },
                                  }}
                                />
                              </Stack>
                              <Typography sx={{
                                display: "flex", alignItems: "center",
                                color: C.textSecondary, fontSize: { xs: "0.7rem", sm: "0.78rem" }, mb: 0.5,
                                fontWeight: 500,
                              }}>
                                <LocationOn sx={{ fontSize: 13, mr: 0.5 }} />
                                {learner.location || "Location not set"}
                              </Typography>
                              {learner.educationLevel && (
                                <Typography sx={{
                                  color: C.primary, fontWeight: 700,
                                  fontSize: { xs: "0.65rem", sm: "0.74rem" },
                                  display: "flex", alignItems: "center",
                                }}>
                                  <School sx={{ fontSize: 13, mr: 0.5 }} />
                                  {learner.educationLevel}
                                </Typography>
                              )}
                            </Box>
                          </Stack>

                          {/* Card action buttons */}
                          <Stack direction="row" spacing={1} mt={1.5}>
                            <Button
                              variant="outlined" size="small"
                              sx={{
                                textTransform: "none", borderRadius: 2,
                                borderColor: C.border, color: C.textSecondary,
                                flex: 1, fontWeight: 700,
                                fontSize: { xs: "0.7rem", sm: "0.76rem" }, py: 0.5,
                                "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySoft },
                              }}
                            >
                              View
                            </Button>
                            <Button
                              onClick={(e) => { e.stopPropagation(); navigate("/chat"); }}
                              variant="contained" size="small"
                              sx={{
                                textTransform: "none", borderRadius: 2,
                                bgcolor: C.primary, color: "#fff",
                                flex: 1, fontWeight: 700,
                                fontSize: { xs: "0.7rem", sm: "0.76rem" }, py: 0.5,
                                boxShadow: "none",
                                "&:hover": { bgcolor: C.primaryDark, boxShadow: `0 2px 8px ${C.primary}30` },
                              }}
                            >
                              Message
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Box>

              {/* Pagination */}
              {!loading && !error && paginatedLearners.length > 0 && (
                <Box sx={{ pt: 2, borderTop: `1px solid ${C.borderSoft}` }}>
                  <Stack direction="row" spacing={2} justifyContent="space-between">
                    <Button
                      size="small" variant="outlined" disabled={!hasPrev}
                      onClick={() => setPage((p) => Math.max(p - 1, 0))}
                      startIcon={<ArrowBack />}
                      sx={{
                        textTransform: "none", borderRadius: 2,
                        flex: 1, fontWeight: 700, fontSize: { xs: 11, sm: 13 },
                        borderColor: hasPrev ? C.primary : C.borderSoft,
                        color: hasPrev ? C.primary : C.textMuted,
                        "&:hover": {
                          borderColor: hasPrev ? C.primaryDark : C.borderSoft,
                          bgcolor: hasPrev ? C.primarySoft : "transparent",
                        },
                      }}
                    >
                      Prev
                    </Button>
                    <Button
                      size="small" variant="outlined" disabled={!hasNext}
                      onClick={() => setPage((p) => p + 1)}
                      endIcon={<ArrowForward />}
                      sx={{
                        textTransform: "none", borderRadius: 2,
                        flex: 1, fontWeight: 700, fontSize: { xs: 11, sm: 13 },
                        borderColor: hasNext ? C.primary : C.borderSoft,
                        color: hasNext ? C.primary : C.textMuted,
                        "&:hover": {
                          borderColor: hasNext ? C.primaryDark : C.borderSoft,
                          bgcolor: hasNext ? C.primarySoft : "transparent",
                        },
                      }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              )}
            </Paper>

            {/* ── RIGHT: Detail panel — desktop only ─────────────────── */}
            {!isMobile && (
              <Box sx={{
                position: "relative",
                flexGrow: 1, minWidth: 0,
                bgcolor: C.paper, borderRadius: 3,
                border: `1px solid ${C.border}`,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                p: { sm: 3, md: 4 },
                minHeight: { lg: "85vh" },
                overflowY: "auto",
                boxSizing: "border-box",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
                },
              }}>
                <Box sx={{ pt: 0.5 }}>
                  <DetailPanel />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Mobile/Tablet: detail in bottom drawer ─────────────── */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              maxHeight: "90vh",
              overflowY: "auto",
              px: { xs: "16px", sm: "24px" },
              pt: "16px",
              pb: "32px",
            },
          }}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: C.borderSoft, borderRadius: 2, mx: "auto", mb: 2.5 }} />
          <DetailPanel />
        </Drawer>
      )}

      <Footer />
    </Box>
  );
}